import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  Loader2,
  Gift,
  ShoppingBag,
  TreePine,
  Star,
  Zap,
  Heart
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

interface Reward {
  id: string;
  title: string;
  description: string;
  cost: number;
  category: 'discount' | 'donation' | 'badge' | 'experience';
  image_url: string;
  icon: string;
  available: number;
  active: boolean;
  created_at: string;
}

const mockRewards: Reward[] = [
  {
    id: 'reward-1',
    title: '20% Off Eco-Friendly Products',
    description: 'Get 20% discount at EcoShop for sustainable products',
    cost: 500,
    category: 'discount',
    image_url: 'https://images.pexels.com/photos/3962294/pexels-photo-3962294.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
    icon: 'ShoppingBag',
    available: 50,
    active: true,
    created_at: '2024-12-08T00:00:00Z'
  },
  {
    id: 'reward-2',
    title: 'Plant 5 Trees',
    description: 'We\'ll donate to plant 5 trees in your name through our partner organizations',
    cost: 1000,
    category: 'donation',
    image_url: 'https://images.pexels.com/photos/1072824/pexels-photo-1072824.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
    icon: 'TreePine',
    available: 25,
    active: true,
    created_at: '2024-12-08T00:00:00Z'
  }
];

export default function AdminRewards() {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const emptyReward: Omit<Reward, 'id' | 'created_at'> = {
    title: '',
    description: '',
    cost: 100,
    category: 'discount',
    image_url: '',
    icon: 'Gift',
    available: 10,
    active: true
  };

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      
      if (!isSupabaseConfigured) {
        setRewards(mockRewards);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('rewards')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching rewards:', error);
        setRewards(mockRewards);
        return;
      }

      setRewards(data || []);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      setRewards(mockRewards);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (reward: Reward | Omit<Reward, 'id' | 'created_at'>) => {
    try {
      setSaving(true);

      if (!isSupabaseConfigured) {
        if ('id' in reward) {
          // Update existing
          setRewards(prev => prev.map(r => r.id === reward.id ? reward : r));
        } else {
          // Create new
          const newReward = {
            ...reward,
            id: `reward-${Date.now()}`,
            created_at: new Date().toISOString()
          };
          setRewards(prev => [newReward, ...prev]);
        }
        setEditingReward(null);
        setIsCreating(false);
        setSaving(false);
        return;
      }

      if ('id' in reward) {
        // Update existing reward
        const { error } = await supabase
          .from('rewards')
          .update(reward)
          .eq('id', reward.id);

        if (error) {
          console.error('Error updating reward:', error);
          return;
        }
      } else {
        // Create new reward
        const { error } = await supabase
          .from('rewards')
          .insert(reward);

        if (error) {
          console.error('Error creating reward:', error);
          return;
        }
      }

      await fetchRewards();
      setEditingReward(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving reward:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rewardId: string) => {
    if (!confirm('Are you sure you want to delete this reward?')) return;

    try {
      if (!isSupabaseConfigured) {
        setRewards(prev => prev.filter(r => r.id !== rewardId));
        return;
      }

      const { error } = await supabase
        .from('rewards')
        .delete()
        .eq('id', rewardId);

      if (error) {
        console.error('Error deleting reward:', error);
        return;
      }

      await fetchRewards();
    } catch (error) {
      console.error('Error deleting reward:', error);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'discount': return 'bg-blue-100 text-blue-800';
      case 'donation': return 'bg-green-100 text-green-800';
      case 'badge': return 'bg-purple-100 text-purple-800';
      case 'experience': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Rewards Management</h2>
          <p className="text-gray-600 mt-2">Create and manage rewards for the EcoCoin store</p>
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Add Reward</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <Gift className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{rewards.length}</p>
              <p className="text-sm text-gray-600">Total Rewards</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <ShoppingBag className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {rewards.filter(r => r.category === 'discount').length}
              </p>
              <p className="text-sm text-gray-600">Discounts</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <TreePine className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {rewards.filter(r => r.category === 'donation').length}
              </p>
              <p className="text-sm text-gray-600">Donations</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <div className="w-6 h-6 bg-yellow-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">Σ</span>
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {Math.round(rewards.reduce((sum, r) => sum + r.cost, 0) / rewards.length) || 0}
              </p>
              <p className="text-sm text-gray-600">Avg Cost</p>
            </div>
          </div>
        </div>
      </div>

      {/* Reward Form Modal */}
      {(editingReward || isCreating) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingReward ? 'Edit Reward' : 'Create New Reward'}
              </h3>
              <button
                onClick={() => {
                  setEditingReward(null);
                  setIsCreating(false);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <RewardForm
              reward={editingReward || emptyReward}
              onSave={handleSave}
              saving={saving}
            />
          </div>
        </div>
      )}

      {/* Rewards Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {rewards.map((reward) => {
          const IconComponent = (LucideIcons as any)[reward.icon] || Gift;
          
          return (
            <div key={reward.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={reward.image_url}
                  alt={reward.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(reward.category)}`}>
                    {reward.category}
                  </span>
                </div>
                {!reward.active && (
                  <div className="absolute inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
                    <span className="text-white font-bold">Inactive</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <IconComponent className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">{reward.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{reward.description}</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-lg font-bold text-gray-900">
                        {reward.cost.toLocaleString()} EcoCoins
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {reward.available} available
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingReward(reward)}
                      className="p-2 text-gray-600 hover:text-emerald-600 rounded-lg transition-colors"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(reward.id)}
                      className="p-2 text-gray-600 hover:text-red-600 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Reward Form Component
function RewardForm({ 
  reward, 
  onSave, 
  saving 
}: { 
  reward: Reward | Omit<Reward, 'id' | 'created_at'>;
  onSave: (reward: Reward | Omit<Reward, 'id' | 'created_at'>) => void;
  saving: boolean;
}) {
  const [formData, setFormData] = useState(reward);

  const categories = ['discount', 'donation', 'badge', 'experience'];
  const iconOptions = ['Gift', 'ShoppingBag', 'TreePine', 'Star', 'Zap', 'Heart', 'Award', 'Target'];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          rows={3}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
        <input
          type="url"
          value={formData.image_url}
          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          placeholder="https://example.com/image.jpg"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Cost (EcoCoins)</label>
          <input
            type="number"
            value={formData.cost}
            onChange={(e) => setFormData({ ...formData, cost: parseInt(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            min="0"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Available</label>
          <input
            type="number"
            value={formData.available}
            onChange={(e) => setFormData({ ...formData, available: parseInt(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            min="0"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
          <select
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {iconOptions.map(icon => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm text-gray-700">Active</span>
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="submit"
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>Save Reward</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}